import { useQuery } from "@tanstack/react-query";
import { Location } from "~/db/types";
import {
  checkIfLocationsAreInWatchList,
  getLocationFromWatchList,
  getPaginatedWatchList,
  getAllWatchList,
} from "~/lib/server-functions/watchlist";
import { QUERY_KEYS } from "~/lib/tanstack-query/hooks/queryKeys";
import { LocationWithImages, getLocationsByIds } from "~/lib/server-functions/get-locations";
import { useAuthentication } from "~/lib/better-auth/client";
import {
  isInWatchlistStorage,
  getWatchlistLocationIds,
  getWatchlistFromStorage,
} from "~/lib/storage/watchlist-storage";

export const useGetLocationFromWatchList = (locationId?: number) => {
  const { isAuthenticated } = useAuthentication();
  
  return useQuery<Location | null>({
    queryKey: [QUERY_KEYS.WATCH_LIST, locationId],
    queryFn: async () => {
      if (!locationId) {
        return null;
      }
      
      if (!isAuthenticated) {
        // For anonymous users, check localStorage
        return isInWatchlistStorage(locationId) ? ({} as Location) : null;
      }
      
      const res = await getLocationFromWatchList({ data: { locationId } });

      if (!res) {
        return null;
      }

      return res;
    },
  });
};

export const useCheckIfLocationsAreInWatchList = (locationIds: number[]) => {
  const { isAuthenticated } = useAuthentication();
  
  return useQuery({
    queryKey: [QUERY_KEYS.WATCH_LIST, ...locationIds],
    queryFn: async () => {
      if (!isAuthenticated) {
        // For anonymous users, check localStorage
        const storedIds = getWatchlistLocationIds();
        return locationIds.filter((id) => storedIds.includes(id));
      }
      
      const res = await checkIfLocationsAreInWatchList({
        data: { locationIds },
      });

      return res ?? [];
    },
  });
};

export const usePaginatedWatchList = (page: number, pageSize: number) => {
  const { isAuthenticated } = useAuthentication();
  
  return useQuery({
    queryKey: [QUERY_KEYS.WATCH_LIST, "paginated", page, pageSize],
    queryFn: async () => {
      if (!isAuthenticated) {
        // For anonymous users, get from localStorage and fetch location data
        const storedItems = getWatchlistFromStorage();
        const total = storedItems.length;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedItems = storedItems.slice(startIndex, endIndex);
        const locationIds = paginatedItems.map((item) => item.locationId);
        
        if (locationIds.length === 0) {
          return {
            locations: [],
            total: 0,
            page,
            pageSize,
            totalPages: 0,
          };
        }
        
        const locations = await getLocationsByIds({ data: locationIds });
        
        return {
          locations,
          total,
          page,
          pageSize,
          totalPages: Math.ceil(total / pageSize),
        };
      }
      
      return await getPaginatedWatchList({ data: { page, pageSize } });
    },
  });
};

export const useAllWatchList = () => {
  const { isAuthenticated } = useAuthentication();
  
  return useQuery<LocationWithImages[]>({
    queryKey: [QUERY_KEYS.WATCH_LIST, "all"],
    queryFn: async () => {
      if (!isAuthenticated) {
        // For anonymous users, get from localStorage and fetch location data
        const locationIds = getWatchlistLocationIds();
        
        if (locationIds.length === 0) {
          return [];
        }
        
        return await getLocationsByIds({ data: locationIds });
      }
      
      const res = await getAllWatchList();
      return res ?? [];
    },
  });
};
